<template>
  <div>
    <PageAllow controller="SubAccountAnalyses" action="mfShopSaleView" />
    <Breadcrumbs :items="items" />

    <v-slide-x-transition>
      <v-sheet class="transparent-background-color pa-4">
        <v-expansion-panels v-model="panels" multiple accordion>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-card-title class="ms-n4 py-0">
                {{ $t("AlliancesSubAccounts.OrderInfo") }}
                <v-spacer />
              </v-card-title>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-divider />
              <v-row no-gutters class="mt-4 table-style">
                <v-col cols="12" md="4">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("AlliancesSubAccounts.DetailFields.order_code") }}
                      {{ $t("Global.colon") }}
                    </span>
                    {{ order.orderno }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{
                        $t("AlliancesSubAccounts.DetailFields.shop", [
                          $consts.shopName(),
                        ])
                      }}
                      {{ $t("Global.colon") }}
                    </span>
                    【{{ order.branchcode }}】
                    {{ order.branchname }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("AlliancesSubAccounts.DetailFields.created_time") }}
                      {{ $t("Global.colon") }}
                    </span>
                    {{ order.saletime | formatTime }}
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-card class="mt-4">
          <v-data-table
            :headers="payDetailHeaders"
            :items="pay_details"
            class="elevation-2 px-4 pb-2"
            disable-pagination
            hide-default-footer
          >
            <template v-slot:top class="transparent-background-color">
              <v-toolbar flat>
                <v-toolbar-title class="ms-n2">
                  {{ $t("AlliancesSubAccounts.PayDetails") }}
                </v-toolbar-title>
              </v-toolbar>
              <v-divider />
            </template>
            <template v-slot:item.payamount="{ item }">
              {{$t("Global.money")}}
              {{ item.payamount | formatMoney}}
            </template>
            <template v-slot:item.branchcode="{ item }">
              {{ item.branchcode }} /
              {{ item.branchname }}
            </template>
            <template v-slot:item.paytime="{ item }">
              {{ item.paytime | formatTime }}
            </template>
          </v-data-table>
        </v-card>

        <v-card class="mt-4">
          <v-data-table
            :headers="subAccountDetailHeaders"
            :items="sub_account_details"
            :expanded.sync="expanded"
            item-key="id"
            show-expand
            class="elevation-2 px-4 pb-2"
            disable-pagination
            hide-default-footer
          >
            <template v-slot:top class="transparent-background-color">
              <v-toolbar flat>
                <v-toolbar-title class="ms-n2">
                  {{ $t("AlliancesSubAccounts.SubAccountInfo") }}
                </v-toolbar-title>
              </v-toolbar>
              <v-divider />
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row class="caption">
                  <v-col cols="12" md="4">
                    <span class="text-left">
                      {{ $t("AlliancesSubAccounts.DetailFields.voucher_code") }}
                      {{ $t("Global.colon") }}
                    </span>
                    {{ item.pay_detail.ordercode }}
                  </v-col>
                  <v-col cols="12" md="4">
                    <span class="text-left">
                    {{ item.pay_detail.payname }}{{ $t("Global.colon") }}
                    </span>
                    {{ $t("Global.money") }}{{ item.pay_detail.payamount | formatMoney }}
                  </v-col>
                  <v-col cols="12" md="4">
                    <span class="text-left">
                      {{ $t("AlliancesSubAccounts.DetailFields.pay_time")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ item.pay_detail.paytime | formatTime }}
                  </v-col>
                </v-row>
              </td>
            </template>
            <template v-slot:item.account_type="{ item }">
              {{ item.account_type }}-
              {{ item.sub_account_name }}
            </template>
            <template v-slot:item.sub_account_money="{ item }">
              {{ $t("Global.money") }}
              {{ item.sub_account_money | formatMoney }}
            </template>
            <template v-slot:item.sub_account_time="{ item }">
              {{ item.sub_account_time | formatTime }}
            </template>
          </v-data-table>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="order_details"
          :loading="loading"
          multi-sort
          :search="filter"
          class="elevation-2 px-4 mt-4 pb-2"
          disable-pagination
          hide-default-footer
          :no-data-text="$t('AlliancesSubAccounts.DetailNoData')"
        >
          <template v-slot:top>
            <v-toolbar flat class="transparent-background-color">
              <v-toolbar-title class="ms-n2">
                {{ $t("AlliancesSubAccounts.OrderDetails") }}
              </v-toolbar-title>
            </v-toolbar>
            <v-divider />
            <v-toolbar
              flat
              class="transparent-background-color"
              align-self="center"
            >
              <span class="caption secondary--text pt-2">
                {{
                  $t(
                    "$vuetify.dataFooter.pageText",
                    order_details
                      ? [1, order_details.length, order_details.length]
                      : [0, 0, 0]
                  )
                }}
              </span>
              <v-spacer />
              <v-row no-gutters class="me-n3">
                <v-spacer />
                <v-col cols="12" sm="9" md="8">
                  <v-text-field
                    v-model="filter"
                    clearable
                    hide-details
                    :label="$t('Search.filter')"
                  />
                </v-col>
              </v-row>
            </v-toolbar>
          </template>
          <template v-slot:item.commodityname="{ item }">
            {{ item.commodityname }}
            <br />
            <span v-if="item.specvalue" class="caption secondary--text">
              {{ $t("AlliancesSubAccounts.DetailFields.material_spec") }}
              {{ $t("Global.colon") }}
              {{ item.specvalue }}
            </span>
          </template>
          <template v-slot:item.salenums="{ item }">
            {{ item.salenums | formatTwoDot }}
          </template>
          <template v-slot:item.saleprice="{ item }">
            {{ $t("Global.money") }}
            {{ item.saleprice | formatMoney }}
          </template>
          <template v-slot:item.paysaleprice="{ item }">
            {{ $t("Global.money") }}
            {{ item.paysaleprice | formatMoney }}
          </template>
          <template v-slot:item.subtotal="{ item }">
            {{ $t("Global.money") }}
            {{ item.subtotal | formatMoney }}
          </template>
          <template v-slot:item.paysubtotal="{ item }">
            {{ $t("Global.money") }}
            {{ item.paysubtotal | formatMoney }}
          </template>
          <template v-slot:item.returnnums="{ item }">
            {{ item.returnnums | formatTwoDot }}
          </template>
          <template v-slot:item.returnamount="{ item }">
            {{ $t("Global.money") }}
            {{ item.returnamount | formatMoney }}
          </template>
        </v-data-table>
      </v-sheet>
    </v-slide-x-transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import consts from "@/consts";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Authorizations from "@/api/authorizations";
import AllianceSubAccounts from "@/api/alliance_sub_accounts";

export default {
  components: {
    PageAllow,
    Breadcrumbs,
  },

  computed: {
    isDark() {
      return (
        localStorage.getItem("dark") && localStorage.getItem("dark") != "false"
      );
    },
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.alliancesSubAccounts", [
          consts.shopName(),
          consts.allianceName(),
        ]),
        disabled: false,
        href: "/alliance-sub-accounts",
      },
      {
        text: i18n.t("Nav.alliancesSubAccountDetail", [
          consts.shopName(),
          consts.allianceName(),
        ]),
        disabled: true,
        href: "/alliance-sub-accounts/detail",
      },
    ],

    subAccountDetailHeaders() {
      return [
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sub_account_type"),
          align: "start",
          sortable: false,
          value: "account_type",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sub_account_money"),
          align: "end",
          sortable: false,
          value: "sub_account_money",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sub_account_time"),
          align: "center",
          sortable: false,
          value: "sub_account_time",
        },
        { text: "", value: "data-table-expand" },
      ];
    },

    payDetailHeaders() {
      return [
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.voucher_code"),
          align: "start",
          sortable: false,
          value: "ordercode",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.pay_name"),
          align: "center",
          sortable: false,
          value: "payname",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.pay_price"),
          align: "end",
          sortable: false,
          value: "payamount",
        },
        // {
        //   text: i18n.t("AlliancesSubAccounts.DetailFields.pay_shop", [
        //     consts.shopName(),
        //   ]),
        //   align: "start",
        //   sortable: false,
        //   value: "branchcode",
        // },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.pay_time"),
          align: "center",
          sortable: false,
          value: "paytime",
        },
      ];
    },

    headers() {
      return [
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.material_category"),
          align: "start",
          sortable: false,
          value: "categoryname",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.material_name"),
          align: "start",
          sortable: false,
          value: "commodityname",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sale_num"),
          align: "end",
          sortable: false,
          value: "salenums",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sale_price"),
          align: "end",
          sortable: false,
          value: "saleprice",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.pay_sale_price"),
          align: "end",
          sortable: false,
          value: "paysaleprice",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.sub_total"),
          align: "end",
          sortable: false,
          value: "subtotal",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.pay_sub_total"),
          align: "end",
          sortable: false,
          value: "paysubtotal",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.return_number"),
          align: "end",
          sortable: false,
          value: "returnnums",
        },
        {
          text: i18n.t("AlliancesSubAccounts.DetailFields.return_price"),
          align: "end",
          sortable: false,
          value: "returnamount",
        },
      ];
    },

    ...mapState(["loading", "settings", "accessToken"]),
  },

  data: () => ({
    panels: [0],
    expanded: [],
    order: {},
    filter: "",
    order_details: [],
    sub_account_details: [],
    pay_details: [],
  }),

  mounted() {
    this.getAllianceSubAccountDetails();
  },

  methods: {
    checkAccessToken() {
      Authorizations.checkAccessTokenToMid({
        yywd_access_token: this.accessToken,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            localStorage.setItem("midAccessToken", response.data.access_token);
            this.getAllianceSubAccountDetails();
          }
        }.bind(this)
      );
    },
    getAllianceSubAccountDetails() {
      if (
        localStorage.getItem("alliance_sub_account_ticketcode") &&
        localStorage.getItem("alliance_sub_account_branchcode")
      ) {
        AllianceSubAccounts.fetchOne({
          ticketcode: localStorage.getItem("alliance_sub_account_ticketcode"),
          branchcode: localStorage.getItem("alliance_sub_account_branchcode"),
        }).then(
          function (response) {
            if (response.data.status.isOK()) {
              this.order = response.data.order;
              this.order_details = response.data.order.order_details;
              this.sub_account_details =
                response.data.order.sub_account_details;
              this.pay_details = response.data.order.pay_details;
            } else if (response.data.error.code == "ERR667") {
              this.checkAccessToken();
            }
          }.bind(this)
        );
      }
    },
  },
};
</script>